import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useSession } from "../lib/session";
import { useAsync } from "../lib/useAsync";
import { useAppConfig } from "../lib/appConfig";
import { IconChat, IconEye, IconHeart, IconNext, IconPlus, IconRocket, IconScan, IconShield } from "../ui/icons";
import { AsyncButton, Choice, Empty, ErrorState, Loading, PageHeader, TrustBadges, TrustLine, VerdictTag, useToast, type Trust, type Verdict } from "../ui/kit";

type Dash = {
  totals: { active: number; reserved: number; drafts: number; expired: number; views7d: number; views30d: number; saves30d: number; leads30d: number; deals_open: number; sold30d: number; sold30d_cents: number; needsYou: number };
  needs: { conversationId: string; listingTitle: string; buyerName: string; at: string; needs: "answer_price" | "confirm_handover" | "reply" }[];
  trust: Trust;
  listings: {
    id: string;
    status: string;
    title: string;
    priceCents: number | null;
    currency: string;
    quantityAvailable: number;
    quantityTotal: number;
    coverUrl: string | null;
    views: number;
    views7d: number;
    saves: number;
    leads: number;
    openDeals: number;
    expiresAt: string | null;
    bumpAvailableAt: string | null;
    priceVerdict: Verdict | null;
  }[];
};

/** The seller's home: what's live, what people do with it, which deals need them, and one-tap tools. */
export function SellHub() {
  const { t, money, relative } = useI18n();
  const toast = useToast();
  const { currency } = useAppConfig();
  const { me, loading: sLoading } = useSession();
  const { data, error, loading, reload } = useAsync(() => (me ? api<Dash>("/api/seller/dashboard") : Promise.resolve(null)), [me?.id]);
  if (!sLoading && !me) return <Navigate to="/signin?next=/sell" replace />;
  const d = data;
  const now = Date.now();

  return (
    <div>
      <PageHeader title={t("sell.title")} actions={<Link className="btn primary sm" to="/sell/new"><IconPlus size={18} /> {t("sell.new")}</Link>} />
      {loading && !d ? (
        <Loading />
      ) : error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : !d || !d.listings.length ? (
        <Empty title={t("sell.empty.title")} body={t("sell.empty.body")} action={<Link className="btn primary" to="/sell/new">{t("sell.new")}</Link>} />
      ) : (
        <div className="stack-lg">
          <p className="free-note small"><IconShield size={16} /> {t("sell.free")}</p>
          <div className="kpis">
            <div className="kpi"><span className="small muted">{t("dash.live")}</span><b>{d.totals.active}</b>{d.totals.reserved ? <span className="tiny muted">{t("dash.reservedN", { n: d.totals.reserved })}</span> : null}</div>
            <div className="kpi"><span className="small muted">{t("dash.views7d")}</span><b>{d.totals.views7d}</b><span className="tiny muted">{t("dash.views30d", { n: d.totals.views30d })}</span></div>
            <div className="kpi"><span className="small muted">{t("dash.leads")}</span><b>{d.totals.leads30d}</b><span className="tiny muted">{t("dash.saves", { n: d.totals.saves30d })}</span></div>
            <div className="kpi"><span className="small muted">{t("dash.sold30d")}</span><b>{d.totals.sold30d}</b><span className="tiny muted">{money(d.totals.sold30d_cents, currency)}</span></div>
          </div>

          <div className="card stack">
            <div className="row between wrap">
              <strong>{t("dash.yourRecord")}</strong>
              <Link className="link small" to={`/s/${me!.id}`}>{t("dash.publicProfile")}</Link>
            </div>
            <span className="small"><TrustLine trust={d.trust} /></span>
            <TrustBadges trust={d.trust} />
            {!d.trust.badges.includes("fast_reply") && <span className="tiny muted">{t("dash.fastTip")}</span>}
          </div>

          {d.needs.length > 0 && (
            <section aria-labelledby="needs-title">
              <h2 id="needs-title" className="section-title">{t("dash.needsYou", { n: d.totals.needsYou })}</h2>
              <div className="list">
                {d.needs.map((n) => (
                  <Link key={n.conversationId} className="list-item deal-item needs" to={`/inbox/${n.conversationId}`}>
                    <div className="grow">
                      <div className="row between"><span className="t" dir="auto">{n.buyerName}</span><span className="tiny muted">{relative(n.at)}</span></div>
                      <div className="s" dir="auto">{n.listingTitle}</div>
                      <span className="tag needs">{t(`needs.${n.needs}`)}</span>
                    </div>
                    <IconNext size={18} className="muted" />
                  </Link>
                ))}
              </div>
            </section>
          )}

          <section aria-labelledby="mine-title">
            <h2 id="mine-title" className="section-title">{t("dash.listings")}</h2>
            <div className="list">
              {d.listings.map((l) => {
                const canBump = l.bumpAvailableAt && new Date(l.bumpAvailableAt).getTime() <= now;
                const expiresSoon = l.status === "active" && l.expiresAt && new Date(l.expiresAt).getTime() - now < 5 * 86_400_000;
                return (
                  <div key={l.id} className="list-item seller-row">
                    <Link to={l.status === "draft" ? `/sell/${l.id}` : `/sell/${l.id}/manage`} className="row grow" style={{ textDecoration: "none", color: "inherit", minWidth: 0 }}>
                      {l.coverUrl ? <img src={l.coverUrl} alt="" /> : <div className="ph" />}
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div className="t" dir="auto">{l.title}</div>
                        <div className="s">
                          <span className={`tag ${l.status === "active" ? "good" : l.status === "removed" || l.status === "expired" ? "bad" : ""}`}>{t(`sell.status.${l.status}` as MessageKey)}</span>{" "}
                          {l.priceCents != null && <span className="num">{money(l.priceCents, l.currency)}</span>}
                          {l.status !== "draft" && ` · ${l.quantityAvailable}/${l.quantityTotal}`}
                        </div>
                        {l.status !== "draft" && (
                          <div className="row wrap stats-line tiny muted">
                            <span><IconEye size={14} /> {l.views}</span>
                            <span><IconHeart size={14} /> {l.saves}</span>
                            <span><IconChat size={14} /> {l.leads}</span>
                            {l.openDeals > 0 && <span className="tag offer">{t("dash.openDeals", { n: l.openDeals })}</span>}
                            <VerdictTag v={l.priceVerdict} size="sm" />
                            {expiresSoon && <span className="tag bad">{t("dash.expires", { d: relative(l.expiresAt!) })}</span>}
                          </div>
                        )}
                      </div>
                    </Link>
                    {canBump && (
                      <AsyncButton
                        className="btn sm"
                        onClick={async () => {
                          await api(`/api/listings/${l.id}/bump`, { body: {} });
                          toast(t("tools.bumped"));
                          await reload();
                        }}
                      >
                        <IconRocket size={16} /> {t("tools.bump")}
                      </AsyncButton>
                    )}
                    {l.status === "expired" && (
                      <AsyncButton className="btn sm primary" onClick={async () => { await api(`/api/listings/${l.id}/resume`, { body: {} }); toast(t("tools.renewed")); await reload(); }}>
                        {t("tools.renew")}
                      </AsyncButton>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}

export function SellNew() {
  const { t, locale } = useI18n();
  const { me, loading } = useSession();
  const nav = useNavigate();
  // Staff open and close categories in the console; the choice only appears once there's more than one.
  const { data: open } = useAsync(() => api<{ categories: { key: string; labelEn: string; labelAr: string }[] }>("/api/catalog/categories"), []);
  const [picked, setPicked] = useState<string | null>(null);
  const category = picked ?? open?.categories[0]?.key ?? "phone";
  const [mode, setMode] = useState<"ai" | "manual">("ai");
  if (!loading && !me) return <Navigate to="/signin?next=/sell/new" replace />;
  return (
    <div style={{ maxWidth: 560 }}>
      <PageHeader title={t("sell.new")} back="/sell" />
      {open && open.categories.length > 1 && (
        <>
          <h2 className="section-title">{t("sell.what")}</h2>
          <div className="stack" style={{ marginBottom: "var(--s6)" }}>
            {open.categories.map((c) => (
              <Choice key={c.key} selected={category === c.key} onClick={() => setPicked(c.key)} title={locale === "ar" ? c.labelAr : c.labelEn} />
            ))}
          </div>
        </>
      )}
      <h2 className="section-title">{t("sell.how")}</h2>
      <div className="stack">
        <Choice selected={mode === "ai"} onClick={() => setMode("ai")} icon={<IconScan />} title={t("sell.mode.ai")} hint={t("sell.mode.ai.hint")} />
        <Choice selected={mode === "manual"} onClick={() => setMode("manual")} title={t("sell.mode.manual")} hint={t("sell.mode.manual.hint")} />
      </div>
      <div className="sticky-cta">
        <AsyncButton
          className="btn primary block"
          onClick={async () => {
            const r = await api<{ id: string }>("/api/listings", { body: { category, mode } });
            nav(`/sell/${r.id}`, { replace: true });
          }}
        >
          {t("sell.start")}
        </AsyncButton>
      </div>
    </div>
  );
}
